import React, { useState } from 'react';
// Removed react-icons import to avoid TypeScript issues

interface Table3Data {
  title: string;
  company: string;
  headers: string[];
  rows: Array<{
    // Single brand format
    productName?: string;
    stock?: number;
    rate?: number;
    amount?: number;
    productId?: string | null;
    entryId?: string | null;
    // Dual brand format (legacy)
    aquafitProductName?: string;
    aquafitStock?: number;
    aquafitRate?: number;
    aquafitAmount?: number;
    aquafitProductId?: string | null;
    aquafitEntryId?: string | null;
    cablefitProductName?: string;
    cablefitStock?: number;
    cablefitProductId?: string | null;
  }>;
}

interface InventoryTable3Props {
  data: Table3Data;
  canEdit: boolean;
  onUpdate: (updates: any[]) => void;
  saving: boolean;
}

const InventoryTable3: React.FC<InventoryTable3Props> = ({ data, canEdit, onUpdate, saving }) => {
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; field: string; brand?: 'aquafit' | 'cablefit' } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [pendingUpdates, setPendingUpdates] = useState<any[]>([]);

  // Detect if this is single brand or dual brand format
  const isSingleBrand = data.rows.length > 0 && data.rows[0].productName !== undefined;

  const handleEditStart = (rowIndex: number, field: 'rate', brand?: 'aquafit' | 'cablefit', currentValue?: number) => {
    if (!canEdit) return;
    setEditingCell({ rowIndex, field, brand });
    setEditValue(currentValue?.toString() || '0');
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleEditSave = () => {
    if (!editingCell) return;

    const { rowIndex, field } = editingCell;
    const newValue = parseFloat(editValue) || 0;

    // Add to pending updates
    const existingUpdateIndex = pendingUpdates.findIndex(
      update => update.rowIndex === rowIndex
    );

    const updateData = {
      rowIndex,
      field,
      value: newValue,
      productId: data.rows[rowIndex].aquafitProductId,
      entryId: data.rows[rowIndex].aquafitEntryId
    };

    if (existingUpdateIndex >= 0) {
      const updatedUpdates = [...pendingUpdates];
      updatedUpdates[existingUpdateIndex] = { ...updatedUpdates[existingUpdateIndex], [field]: newValue };
      setPendingUpdates(updatedUpdates);
    } else {
      setPendingUpdates([...pendingUpdates, updateData]);
    }

    setEditingCell(null);
    setEditValue('');
  };

  const handleSaveAll = () => {
    if (pendingUpdates.length === 0) return;

    const updates = pendingUpdates.map(update => ({
      productId: update.productId,
      rate: update.value
    }));

    onUpdate(updates);
    setPendingUpdates([]);
  };

  const getCellValue = (rowIndex: number, field: string, brand?: 'aquafit' | 'cablefit') => {
    const pendingUpdate = pendingUpdates.find(
      update => update.rowIndex === rowIndex && update.field === field && update.brand === brand
    );
    
    if (pendingUpdate) {
      return pendingUpdate.value;
    }
    
    const row = data.rows[rowIndex];
    
    if (isSingleBrand) {
      if (field === 'rate') return row.rate || 0;
    } else {
      if (brand === 'aquafit' && field === 'rate') return row.aquafitRate || 0;
    }
    return 0;
  };

  const getCalculatedAmount = (rowIndex: number, brand?: 'aquafit' | 'cablefit') => {
    const row = data.rows[rowIndex];
    let stock = 0;
    
    if (isSingleBrand) {
      stock = row.stock || 0;
    } else if (brand === 'aquafit') {
      stock = row.aquafitStock || 0;
    }
    
    const rate = getCellValue(rowIndex, 'rate', brand);
    return stock * rate;
  };

  const isEditing = (rowIndex: number, field: string, brand?: 'aquafit' | 'cablefit') => {
    return editingCell?.rowIndex === rowIndex && editingCell?.field === field && editingCell?.brand === brand;
  };

  const hasPendingUpdates = pendingUpdates.length > 0;

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4">
        <h2 className="text-xl font-semibold text-white">Table 3: Stock with Rates and Amounts</h2>
        <p className="text-purple-100 text-sm">{data.title} - {data.company}</p>
      </div>

      {/* Action Bar */}
      {canEdit && hasPendingUpdates && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3">
          <div className="flex items-center justify-between">
            <span className="text-yellow-800 text-sm font-medium">
              {pendingUpdates.length} pending rate changes
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => setPendingUpdates([])}
                className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Cancel All
              </button>
              <button
                onClick={handleSaveAll}
                disabled={saving}
                className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                ✓
                {saving ? 'Saving...' : 'Save All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {data.headers.map((header, index) => (
                <th 
                  key={index}
                  className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    index === 0 || (index === 4 && !isSingleBrand) ? 'text-left' : 'text-center'
                  }`}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50">
                {isSingleBrand ? (
                  // Single brand format
                  <>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-medium">{row.productName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {((row.stock || 0)).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {isEditing(rowIndex, 'rate') ? (
                        <div className="flex items-center space-x-1">
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-24 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                            autoFocus
                            step="0.01"
                            min="0"
                          />
                          <button
                            onClick={handleEditSave}
                            className="text-green-600 hover:text-green-800"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleEditCancel}
                            className="text-red-600 hover:text-red-800"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-1">
                          <span className="text-sm text-gray-900 font-medium">
                            ₹{(getCellValue(rowIndex, 'rate') || 0).toLocaleString()}
                          </span>
                          {canEdit && (
                            <button
                              onClick={() => handleEditStart(rowIndex, 'rate', undefined, getCellValue(rowIndex, 'rate'))}
                              className="text-purple-600 hover:text-purple-800"
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ₹{((getCalculatedAmount(rowIndex) || 0)).toLocaleString()}
                      </span>
                    </td>
                  </>
                ) : (
                  // Dual brand format (legacy)
                  <>
                    {/* AQUAFIT Column */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-medium">{row.aquafitProductName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {(row.aquafitStock || 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {isEditing(rowIndex, 'rate', 'aquafit') ? (
                        <div className="flex items-center space-x-1">
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-24 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                            autoFocus
                            step="0.01"
                            min="0"
                          />
                          <button
                            onClick={handleEditSave}
                            className="text-green-600 hover:text-green-800"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleEditCancel}
                            className="text-red-600 hover:text-red-800"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-1">
                          <span className="text-sm text-gray-900 font-medium">
                            ₹{(getCellValue(rowIndex, 'rate', 'aquafit') || 0).toLocaleString()}
                          </span>
                          {canEdit && (
                            <button
                              onClick={() => handleEditStart(rowIndex, 'rate', 'aquafit', getCellValue(rowIndex, 'rate', 'aquafit'))}
                              className="text-purple-600 hover:text-purple-800"
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ₹{(getCalculatedAmount(rowIndex, 'aquafit') || 0).toLocaleString()}
                      </span>
                    </td>

                    {/* CABLEFIT Column */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-medium">{row.cablefitProductName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {(row.cablefitStock || 0).toLocaleString()}
                      </span>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="bg-gray-50 px-6 py-4">
        {isSingleBrand ? (
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {data.rows.reduce((sum, row) => sum + (row.stock || 0), 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total Stock</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                ₹{data.rows.reduce((sum, row, index) => sum + (getCalculatedAmount(index) || 0), 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total Amount</div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {data.rows.reduce((sum, row) => sum + (row.aquafitStock || 0), 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total AQUAFIT Stock</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                ₹{data.rows.reduce((sum, row, index) => sum + (getCalculatedAmount(index, 'aquafit') || 0), 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total Amount</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {data.rows.reduce((sum, row) => sum + (row.cablefitStock || 0), 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total CABLEFIT Stock</div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-gray-50 px-6 py-3 border-t">
        <p className="text-xs text-gray-600">
          {canEdit ? 'Click the edit icon to modify Rs. (rate) values - Amount will be automatically calculated' : 'Read-only view'}
        </p>
      </div>
    </div>
  );
};

export default InventoryTable3;
